// Single source of the "Condiciones" option lists shared by the bridge
// (V2PrintingPreferencesTab) and native (V2NativePreferencesTab) printing
// preferences. Every destination — ticket, comandas AND stickers — offers the
// same five triggers: shouldAutoPrint (auto-print-policy.ts) is
// destination-agnostic, storage/migration/backup accept all five for every
// destination, and the legacy native modal already let devices persist any of
// them; a shorter per-destination list renders a persisted value as a blank
// listbox (PosUiListbox shows its placeholder when the value is not among its
// items). Do not "fix" a value outside a list by clamping it in a store.
//
// No import from PosUiListbox.vue on purpose: src/definitions.d.ts shims
// '*.vue' as a default-only module, so a named type import from an SFC inside
// a .ts file fails tsc. The item shapes below are structurally assignable to
// IPosUiListboxItem.
import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';

export type TTranslate = (key: string) => string;

export interface IPrintTriggerItem {
  id: TicketPrintSetting;
  label: string;
}

export interface IPrintSourceItem {
  id: PrintSource;
  label: string;
}

/** Display order of the "Imprimir" (auto-print trigger) options. */
export const PRINT_TRIGGER_ORDER: readonly TicketPrintSetting[] = [
  TicketPrintSetting.Manual,
  TicketPrintSetting.OnReceive,
  TicketPrintSetting.OnConfirm,
  TicketPrintSetting.OnFinish,
  TicketPrintSetting.OnPaid,
];

/** Exhaustive by type: a new TicketPrintSetting member without a label is a type error. */
export const PRINT_TRIGGER_LABEL_KEYS: Record<TicketPrintSetting, string> = {
  [TicketPrintSetting.Manual]: 'printingV2.triggerManual',
  [TicketPrintSetting.OnReceive]: 'printingV2.triggerOnReceive',
  [TicketPrintSetting.OnConfirm]: 'printingV2.triggerOnConfirm',
  [TicketPrintSetting.OnFinish]: 'printingV2.triggerOnFinish',
  [TicketPrintSetting.OnPaid]: 'printingV2.triggerOnPaid',
};

/** Display order of the "Para pedidos de" (source) options — narrowest scope first, NOT enum order. */
export const PRINT_SOURCE_ORDER: readonly PrintSource[] = [
  PrintSource.ThisDevice,
  PrintSource.ThisDeviceAndOnline,
  PrintSource.AnyDevice,
];

export const PRINT_SOURCE_LABEL_KEYS: Record<PrintSource, string> = {
  [PrintSource.ThisDevice]: 'printingV2.sourceThisDevice',
  [PrintSource.ThisDeviceAndOnline]: 'printingV2.sourceThisDeviceAndOnline',
  [PrintSource.AnyDevice]: 'printingV2.sourceAnyDevice',
};

export function buildPrintTriggerItems(t: TTranslate): IPrintTriggerItem[] {
  return PRINT_TRIGGER_ORDER.map((id) => ({
    id,
    label: t(PRINT_TRIGGER_LABEL_KEYS[id]),
  }));
}

export function buildPrintSourceItems(t: TTranslate): IPrintSourceItem[] {
  return PRINT_SOURCE_ORDER.map((id) => ({
    id,
    label: t(PRINT_SOURCE_LABEL_KEYS[id]),
  }));
}
